/**
 * SavedCardBottomPanel - 고정 하단 카드 선택 패널
 *
 * 기본적으로 선택된 카드 표시 + 결제하기 버튼.
 * 펼치면 전체 카드 목록과 새 카드 추가 버튼이 나타남.
 *
 * 결제 방식:
 * - card: 저장된 카드 선택
 * - cash: 현금 결제 (카드 없이)
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, CreditCard, Plus, Loader2, Wallet, Gift } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { SavedCard } from '@/lib/api/types';

/**
 * #421 [3]: 미지원 안내 라벨 prop 이 런타임에 누락(undefined/빈 문자열)돼도
 * 빈 안내가 렌더되지 않도록 보장하는 최후 fallback (ko — checkoutTranslations.ko 와 동일 문구).
 * 정상 경로는 CheckoutPage 가 i18n 라벨을 항상 주입하므로 이 상수는 도달하지 않는다.
 */
const FALLBACK_CARD_UNAVAILABLE_LABEL = '이 매장은 카드 결제를 지원하지 않습니다.';
const FALLBACK_CASH_UNAVAILABLE_LABEL = '이 매장은 현금 결제를 지원하지 않습니다.';

interface SavedCardBottomPanelProps {
  savedCards: SavedCard[];
  selectedCardId: number | null;
  onCardSelect: (cardId: number) => void;
  onAddNewCard: () => void;
  onPayCash: () => void;
  onPayCard: () => void;
  isSubmitting: boolean;
  isLoggedIn: boolean;
  isLoadingCards: boolean;
  totalLabel: string;
  cashLabel: string;
  addCardLabel: string;
  noCardsLabel: string;
  cardsLabel: string;
  defaultLabel: string;
  formatCurrency: (n: number) => string;
  totalAmount: number;
  safeAreaBottom: string;
  isPointsFullCover?: boolean;
  onPayPoints?: () => void;
  pointsPayLabel?: string;
  cardPaymentAltLabel?: string;
  onPayCardWithoutPoints?: () => void;
  /** #322: 카드 결제 비활성 사유 — selectedCardId 가 없을 때 인라인 안내 (예: "결제 카드를 선택하세요") */
  selectCardHintLabel?: string;
  /** 매장이 현금 결제를 허용하는지 (availableCombinations 에서 derive) — false 면 Efectivo 버튼 disable */
  cashSupported?: boolean;
  /** 매장이 카드 결제를 허용하는지 (availableCombinations 에서 derive) — false 면 카드 버튼 disable */
  cardSupported?: boolean;
  /** 현금 결제 미지원 매장 안내 라벨 */
  cashUnavailableLabel?: string;
  /** 카드 결제 미지원 매장 안내 라벨 */
  cardUnavailableLabel?: string;
  /** #421: 결제 method (combinations + assignments) fetch 진행 중 — skeleton 노출. cashSupported/cardSupported 가 일시적으로 false 로 derive 되어 깜빡이는 깜빡임 차단 */
  isLoadingPaymentMethods?: boolean;
  /** 온라인 카드 결제처럼 저장 카드 선택이 필요한지. POS/후불 카드는 주문만 접수하므로 false. */
  requiresCardSelection?: boolean;
  /**
   * #666: 후불 테이블 체크아웃 단일 '주문하기' 모드.
   * 후불은 주문 시점 수단 선택이 구속력 없는 힌트(실제 수단은 결제 시점 확정 —
   * 고객 앱 카드 결제 / 현장 현금 actual_combination 정산)라서, 카드/현금 이중
   * 버튼 대신 수단 중립 단일 버튼만 노출한다. onPlaceOrder 와 함께 사용.
   */
  singleOrderMode?: boolean;
  /** #666: 단일 '주문하기' 핸들러 — 내부 기본 조합 산출은 CheckoutPage 책임 */
  onPlaceOrder?: () => void;
  /** #666: 단일 '주문하기' 라벨 (postpayCheckoutButton i18n 재사용) */
  orderLabel?: string;
}

export function SavedCardBottomPanel({
  savedCards,
  selectedCardId,
  onCardSelect,
  onAddNewCard,
  onPayCash,
  onPayCard,
  isSubmitting,
  isLoggedIn,
  isLoadingCards,
  totalLabel,
  cashLabel,
  addCardLabel,
  noCardsLabel,
  cardsLabel,
  defaultLabel,
  formatCurrency,
  totalAmount,
  safeAreaBottom,
  isPointsFullCover = false,
  onPayPoints,
  pointsPayLabel,
  cardPaymentAltLabel,
  onPayCardWithoutPoints,
  selectCardHintLabel,
  cashSupported = true,
  cardSupported = true,
  cashUnavailableLabel,
  cardUnavailableLabel,
  isLoadingPaymentMethods = false,
  requiresCardSelection = true,
  singleOrderMode = false,
  onPlaceOrder,
  orderLabel,
}: SavedCardBottomPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedCard = savedCards.find((c) => c.id === selectedCardId);

  // #421 [3]: i18n 누락 시 silent 빈 안내 방지 — `||` 로 undefined 와 '' 모두 흡수.
  const cardUnavailableNotice = cardUnavailableLabel || FALLBACK_CARD_UNAVAILABLE_LABEL;
  const cashUnavailableNotice = cashUnavailableLabel || FALLBACK_CASH_UNAVAILABLE_LABEL;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-border shadow-lg"
      style={{ paddingBottom: safeAreaBottom }}
    >
      {/* 카드 선택 패널 (펼쳤을 때) — 로그인 사용자만 접근 가능. #666: 단일 주문 모드에서는 카드 선택 자체가 없음 */}
      {!singleOrderMode && requiresCardSelection && isExpanded && (
        <div className="max-w-[600px] mx-auto px-[16px] pt-[16px] pb-[8px]">
          <p className="text-[13px] font-semibold text-muted-foreground mb-[10px]">
            {cardsLabel}
          </p>

          {isLoadingCards ? (
            <div className="flex justify-center py-[16px]">
              <Loader2 className="w-[20px] h-[20px] animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-[8px] max-h-[200px] overflow-y-auto">
              {savedCards.length === 0 && (
                <p className="text-[13px] text-muted-foreground text-center py-[8px]">
                  {noCardsLabel}
                </p>
              )}
              {savedCards.map((card) => (
                <button
                  key={card.id}
                  onClick={() => {
                    onCardSelect(card.id);
                    setIsExpanded(false);
                  }}
                  className={`w-full flex items-center gap-[12px] p-[12px] rounded-[8px] border-2 transition-all text-left ${
                    selectedCardId === card.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <CreditCard className="w-[20px] h-[20px] text-muted-foreground" />
                  <div className="flex-1">
                    <span className="text-[14px] font-medium">
                      {card.display_name || `${card.card_brand} •••• ${card.card_last_four}`}
                    </span>
                    {card.is_default && (
                      <span className="ml-[8px] text-[11px] text-primary font-semibold">
                        [{defaultLabel}]
                      </span>
                    )}
                  </div>
                  {selectedCardId === card.id && (
                    <div className="w-[18px] h-[18px] rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <div className="w-[7px] h-[7px] rounded-full bg-white" />
                    </div>
                  )}
                </button>
              ))}
              <button
                onClick={onAddNewCard}
                className="w-full flex items-center justify-center gap-[8px] p-[12px] rounded-[8px] border-2 border-dashed border-border hover:border-primary transition-all"
              >
                <Plus className="w-[18px] h-[18px] text-muted-foreground" />
                <span className="text-[14px] font-medium text-muted-foreground">
                  {addCardLabel}
                </span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* 하단 버튼 영역 */}
      <div className="max-w-[600px] mx-auto px-[16px] py-[12px]">
        {isLoadingPaymentMethods ? (
          /* #421: 결제 method fetch 진행 중 — skeleton 노출.
             cashSupported/cardSupported 가 일시적 false 로 derive 되어
             버튼 hide + 미지원 문구 깜빡임 차단. */
          <div
            data-testid="payment-methods-skeleton"
            className="space-y-[12px]"
            aria-busy="true"
            aria-live="polite"
          >
            <Skeleton className="w-full h-[52px] rounded-[8px]" />
            <Skeleton className="w-full h-[52px] rounded-[8px]" />
          </div>
        ) : singleOrderMode && onPlaceOrder ? (
          /* #666: 후불 테이블 단일 '주문하기' — 수단 선택 UI 없이 주문만 접수.
             주문 시점의 카드/현금 선택은 구속력 없는 힌트였다:
             - 카드로 주문해도 강제마감 현장정산에서 actual_combination=현금 정산 가능
             - 현금으로 주문해도 세션 합산청구('전체 결제')가 postpay 전부를 카드로 결제
             → 실제 수단은 결제 시점에 확정되므로 단일 버튼으로 주문 접수만 받는다.
             로그인/비로그인 공통 (저장 카드 선택·현금 분기 불필요). */
          <button
            onClick={onPlaceOrder}
            disabled={isSubmitting}
            className="w-full h-[52px] bg-primary text-white font-bold rounded-[8px] hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-[8px]"
          >
            {isSubmitting ? (
              <Loader2 className="w-[20px] h-[20px] animate-spin" />
            ) : (
              <>
                {orderLabel} {formatCurrency(totalAmount)}
              </>
            )}
          </button>
        ) : isLoggedIn ? (
          /* #421 codex R1 — 포인트 전액 결제도 BE 에선 card 조합으로 접수되므로
             cardSupported=false 매장에선 dead button (핸들러가 조합 부재로 조기 return).
             UI 도 fail-closed 로 정합 — 카드 미지원이면 일반 분기로 내려 안내 노출. */
          isPointsFullCover && onPayPoints && cardSupported ? (
            /* 포인트 전액 결제 모드 */
            <div className="space-y-[16px]">
              <button
                onClick={onPayPoints}
                disabled={isSubmitting}
                className="w-full h-[52px] bg-primary text-white font-bold rounded-[8px] hover:bg-primary-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-[8px]"
              >
                {isSubmitting ? (
                  <Loader2 className="w-[20px] h-[20px] animate-spin" />
                ) : (
                  <>
                    <Gift className="w-[20px] h-[20px]" />
                    {pointsPayLabel} {formatCurrency(totalAmount)}
                  </>
                )}
              </button>
              {cashSupported && (
                <button
                  onClick={onPayCash}
                  disabled={isSubmitting}
                  className="w-full h-[44px] border border-border rounded-[8px] text-[13px] text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50 flex items-center justify-center gap-[6px]"
                >
                  <Wallet className="w-[16px] h-[16px]" />
                  {cashLabel}
                </button>
              )}
              {cardSupported && (
                <button
                  onClick={onPayCardWithoutPoints || onPayCard}
                  disabled={isSubmitting}
                  className="w-full h-[44px] border border-border rounded-[8px] text-[13px] text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50 flex items-center justify-center gap-[6px]"
                >
                  <CreditCard className="w-[16px] h-[16px]" />
                  {cardPaymentAltLabel || cashLabel}
                </button>
              )}
            </div>
          ) : (
            /* 기존 카드/현금 결제 모드 — 변경 없음 */
            <>
              <div className="flex gap-[10px]">
                {requiresCardSelection && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-[8px] px-[16px] py-[14px] border-2 border-border rounded-[8px] text-[14px] hover:border-primary/50 transition-all"
                    aria-expanded={isExpanded}
                  >
                    {selectedCard ? (
                      <>
                        <CreditCard className="w-[18px] h-[18px] text-muted-foreground" />
                        <span className="font-medium">
                          •••• {selectedCard.card_last_four}
                        </span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-[18px] h-[18px] text-muted-foreground" />
                        <span className="text-muted-foreground">{cardsLabel}</span>
                      </>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-[16px] h-[16px] text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-[16px] h-[16px] text-muted-foreground" />
                    )}
                  </button>
                )}

                <button
                  onClick={onPayCard}
                  disabled={!cardSupported || (requiresCardSelection && !selectedCardId) || isSubmitting}
                  aria-describedby={
                    requiresCardSelection && !selectedCardId && selectCardHintLabel
                      ? 'pay-card-hint'
                      : undefined
                  }
                  className="flex-1 h-[52px] bg-primary text-white font-bold rounded-[8px] hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-[8px]"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-[20px] h-[20px] animate-spin" />
                  ) : (
                    <>
                      {!requiresCardSelection && (
                        <CreditCard className="w-[20px] h-[20px]" aria-hidden="true" />
                      )}
                      {totalLabel} {formatCurrency(totalAmount)}
                    </>
                  )}
                </button>
              </div>

              {/* 매장 카드 결제 미지원 안내 — fail-closed 사전 차단.
                  #421 [3]: 라벨 누락 시 silent hide 대신 fallback 문구 보장. */}
              {!cardSupported && (
                <p className="mt-[8px] text-[12px] text-destructive text-center" role="status">
                  {cardUnavailableNotice}
                </p>
              )}

              {/* #322: 결제 카드 미선택 시 인라인 안내 */}
              {cardSupported && requiresCardSelection && !selectedCardId && selectCardHintLabel && (
                <p id="pay-card-hint" className="mt-[8px] text-[12px] text-destructive text-center" role="status">
                  {selectCardHintLabel}
                </p>
              )}

              {/* issue #419 시정: 현금 버튼 시각 강화 — 카드 버튼과 동등 visibility 로 변경.
                  매장이 현금 결제 미지원 시 버튼 hide (사전 차단 — UX). */}
              {cashSupported && (
                <button
                  onClick={onPayCash}
                  disabled={isSubmitting}
                  className="w-full mt-[12px] h-[52px] border-2 border-primary text-primary font-bold rounded-[8px] hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-[8px]"
                >
                  <Wallet className="w-[20px] h-[20px]" />
                  {cashLabel} {formatCurrency(totalAmount)}
                </button>
              )}
            </>
          )
        ) : (
          /* 비로그인: Bloomin 카드 일회성 결제 + 현금 버튼
           * ⚠️ 비로그인은 카드 저장(AddCardModal) 불가 — API 인증 필요
           * 대신 직접 카드 결제(handleProceedToPayment('card'))로 연결 */
          <div className="space-y-[16px]">
            {/* issue #419 시정: 비로그인 결제 — 카드/현금 동등 visibility 로 분리.
                매장 미지원 method 는 사전 차단 (hide). */}
            {cardSupported && (
              <button
                onClick={onPayCard}
                disabled={isSubmitting}
                className="w-full h-[52px] bg-primary text-white font-bold rounded-[8px] hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-[8px]"
              >
                {isSubmitting ? (
                  <Loader2 className="w-[20px] h-[20px] animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-[20px] h-[20px]" />
                    {totalLabel} {formatCurrency(totalAmount)}
                  </>
                )}
              </button>
            )}
            {cashSupported && (
              <button
                onClick={onPayCash}
                disabled={isSubmitting}
                className="w-full h-[52px] border-2 border-primary text-primary font-bold rounded-[8px] hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-[8px]"
              >
                <Wallet className="w-[20px] h-[20px]" />
                {cashLabel} {formatCurrency(totalAmount)}
              </button>
            )}
            {/* #421 [3]: 양 라벨 모두 런타임 누락 시에도 빈 안내 대신 fallback 보장
                (기존 우선순위 card 라벨 → cash 라벨 유지, 최후 fallback 은 cash 계열). */}
            {!cardSupported && !cashSupported && (
              <p className="text-[12px] text-destructive text-center" role="status">
                {cardUnavailableLabel || cashUnavailableNotice}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
